/**
 *
 *  User Profile Page
 *  AccountSettings Component
 *
 *  Created by Vladimir Shishlyannikov on 06/03/2023.
 *  Copyright (c) 2023-present iWoo Ltd. All rights reserved.
 *
 */

import React, { useCallback, useRef, useState, useEffect } from "react";
import { FormattedMessage } from "react-intl";

import { useAppState } from "../../../state";
import { getPhoneCode, useSignUpState } from "../../../state/SignUpState";

// Components
import Button from "../../Button/Button";
import Modal from "react-modal";
import PhoneCodeInput from "../../PhoneCodeInput/PhoneCodeInput";
import PasswordConfirmForm from "../../PasswordReset/PasswordReset";
import PasswordResetCodeVerification from "../../PasswordReset/CodeVerification/CodeVerification";
import ScrollToTopOnMount from "../../ScrollToTopOnMount/ScrollToTopOnMount";
import { CountryFlag } from "../../CountryFlag/CountryFlag";

// Hooks
import useTheme from "../../ThemeProvider/useTheme/useTheme";
import useLatest from "../../../hooks/useLatest/useLatest";

// Icons
import LockIcon from "../../../icons/LockIcon";
import LogoutIcon from "../../../icons/LogoutIcon";
import CloseIcon from "../../../icons/CloseIcon";
import BlockerIcon from "../../../icons/BlockerIcon";
import CheckNiceIcon from "../../../icons/CheckNiceIcon";
import GlobeIcon from "../../../icons/GlobeIcon";

// Utils
import {
  clientWidth,
  formatPhoneNumber,
  getCountryInfoByPhoneCode,
  isAndroidDevice,
  isIosDevice,
  isMac,
} from "../../../utils";
import { modalStyles } from "../../../constants";
import { normalizeLanguageCode } from "../../../utils/locale";
import { useRegion } from "../../../context/RegionProvider";

const languages = {
  "en-GB": "United Kingdom",
  "en-US": "United States",
};

export const LanguageItem = ({
  id,
  className,
  disabled,
  active,
  language,
  title,
  onChange,
}) => {
  const handleChange = useCallback(() => {
    onChange && onChange(language);
  }, [onChange]);

  return (
    <div
      className={`${className} tbk-rounded-2xl_ ${
        active
          ? "tbk-shadow-trube_ tbk-bg-basic-white"
          : "tbk-border_ tbk-border-solid_ tbk-border-blue-grey-light_"
      } tbk-cursor-pointer`}
      onClick={handleChange}
    >
      <div className="tbk-p-1">
        <input
          type="radio"
          id={id}
          checked={active}
          disabled={disabled}
          onChange={handleChange}
        />

        <label
          className="tbk-mb-0 tbk-flex tbk-items-center tbk-pl-5"
          htmlFor={id}
        >
          <CountryFlag region={id.replace("en-", "")} />
          <div className="tbk-text-main tbk-ml-1 tbk-grow tbk-overflow-hidden tbk-text-ellipsis tbk-whitespace-nowrap">
            {title}
          </div>

          {active && false ? (
            <div className="tbk-shrink-0 tbk-text-green">
              <CheckNiceIcon />
            </div>
          ) : null}
        </label>
      </div>
    </div>
  );
};

export const AccountSettings = ({ selectedLanguage, onSelectLanguage }) => {
  const { openModal } = useRegion();
  const { theme } = useTheme();

  const { user, error, updateCustomerInfo, deleteCustomerAccount, signOut } =
    useAppState();
  const { authState, setAuthState } = useSignUpState();

  const [phoneCode] = useState(user.phoneCode || "+1");
  const [countryFlag] = useState(
    isAndroidDevice || isMac || isIosDevice
      ? getCountryInfoByPhoneCode(phoneCode)[1]
      : getCountryInfoByPhoneCode(phoneCode)[2]
  );

  const userPhoneNumber =
    user && user.phoneNumber && user.phoneNumber.replace(phoneCode, "");
  const userFirstName = user && user.firstName;
  const userFamilyName = user && user.lastName;
  const userEmail = user && user.email;
  const userMedicalInformation = user && user.medicalInformation;
  const [isChangePasswordModalOpen, setIsChangePasswordModalOpen] =
    useState(false);
  const [isDeleteAccountModalOpen, setIsDeleteAccountModalOpen] =
    useState(false);
  const [isChangeRegionModalOpen, setIsChangeRegionModalOpen] = useState(false);

  const [medicalInformation, setMedicalInformation] = useState(
    userMedicalInformation
  );
  const [firstName, setFirstName] = useState(userFirstName);
  const [lastName, setLastName] = useState(userFamilyName);
  const [email, setEmail] = useState(userEmail);
  const [notificationsConsent, setNotificationsConsent] = useState(true);

  const [infoModalTitle, setInfoModalTitle] = useState("");
  const [infoModalMsg, setInfoModalMsg] = useState(``);
  const [isInfoModalOpen, setIsInfoModalOpen] = useState(false);
  const [hasFirstNameError, setHasFirstNameError] = useState(false);
  const [hasLastNameError, setHasLastNameError] = useState(false);
  const [hasEmailError, setHasEmailError] = useState(false);
  const [medicalInformationError, setMedicalInformationError] = useState(null);

  const medicalInformationRef = useRef();
  const infoModalResolveRef = useRef(null);

  const latestFirstName = useLatest(firstName);
  const latestLastName = useLatest(lastName);
  const latestEmail = useLatest(email);

  const modalStyle =
    clientWidth() < theme.responsive.breakpoints.sm
      ? modalStyles.mobileDefault
      : modalStyles.default;

  const modalCustomStyle =
    clientWidth() < parseInt(theme.screens.sm)
      ? Object.assign(
          {},
          { ...modalStyles.mobileDefault },
          {
            content: {
              ...modalStyles.mobileDefault.content,
              // display: "flex",
              padding: "16px",
            },
          }
        )
      : Object.assign(
          {},
          { ...modalStyles.reviewsModal },
          {
            content: {
              ...modalStyles.reviewsModal.content,
              width: "480px",
              height: "auto",
              padding: "24px 24px 64px",
            },
          }
        );

  useEffect(() => {
    // Dirty hack to close Change Password modal
    if (authState === "login" && isChangePasswordModalOpen) {
      closeChangePasswordModal();
    }
  }, [authState, isChangePasswordModalOpen]); // eslint-disable-line

  useEffect(() => {
    if (userFirstName !== latestFirstName.current) {
      setFirstName(userFirstName);
    }

    if (userFamilyName !== latestLastName.current) {
      setLastName(userFamilyName);
    }

    if (userEmail !== latestEmail.current) {
      setEmail(userEmail);
    }
  }, [userFirstName, userFamilyName, userEmail]);

  const openChangePasswordModal = useCallback(() => {
    setIsChangePasswordModalOpen(true);
  }, []);

  const closeChangePasswordModal = useCallback(() => {
    setIsChangePasswordModalOpen(false);
  }, []);

  const openDeleteAccountModal = useCallback(() => {
    setIsDeleteAccountModalOpen(true);
  }, []);

  const closeDeleteAccountModal = useCallback(() => {
    setIsDeleteAccountModalOpen(false);
  }, []);

  const openChangeRegionModal = useCallback(() => {
    setIsChangeRegionModalOpen(true);
  }, []);

  const closeChangeRegionModal = useCallback(() => {
    setIsChangeRegionModalOpen(false);
  }, []);

  const openInfoModal = useCallback((msg, title = "Sorry") => {
    return new Promise((resolve, reject) => {
      infoModalResolveRef.current = resolve;

      setIsInfoModalOpen(true);
      setInfoModalTitle(title);
      setInfoModalMsg(msg);
    });
  }, []);

  const closeInfoModal = useCallback(() => {
    infoModalResolveRef.current();
    infoModalResolveRef.current = null;

    setIsInfoModalOpen(false);
  }, []);

  const handleMedicalInformationChange = useCallback(
    (e) => {
      const value = e.target.value;
      setMedicalInformation(value);

      // Сбрасываем ошибку при изменении
      if (medicalInformationError) {
        setMedicalInformationError(null);
      }
    },
    [medicalInformationError]
  );

  const handleMedicalInformationBlur = useCallback(
    (e) => {
      const userId = user.id;
      const value = medicalInformationRef.current.value;
      const minLength = 5;
      const maxLength = 128;

      // Проверка длины
      if (value && value.length < minLength) {
        setMedicalInformationError(
          "Medical information must be at least 5 characters"
        );
        return;
      }

      if (value && value.length > maxLength) {
        setMedicalInformationError(
          "Medical information must be no more than 128 characters"
        );
        return;
      }

      // Сбрасываем ошибку если валидация прошла
      setMedicalInformationError(null);

      // Отправляем только если данные изменились и прошли валидацию
      if (
        value &&
        userMedicalInformation !== value &&
        value.length >= minLength &&
        value.length <= maxLength
      ) {
        updateCustomerInfo({ medicalInformation: value }, userId);
      }
    },
    [user, userMedicalInformation, updateCustomerInfo]
  );

  const handleFirstNameChange = useCallback((e) => {
    let value = e.target.value;

    setFirstName(value);

    if (
      value &&
      /^([^0-9]*)$/.test(value) &&
      value.length >= 2 &&
      value.length <= 20
    ) {
      setHasFirstNameError(false);
    } else {
      setHasFirstNameError(true);
    }
  }, []);

  const handleLastNameChange = useCallback((e) => {
    let value = e.target.value;

    setLastName(value);

    if (
      value &&
      /^([^0-9]*)$/.test(value) &&
      value.length >= 2 &&
      value.length <= 20
    ) {
      setHasLastNameError(false);
    } else {
      setHasLastNameError(true);
    }
  }, []);

  const handleEmailChange = useCallback((e) => {
    let value = e.target.value;

    setEmail(value);

    if (value && /^[^@]+@[^@]+\.[^@]+$/.test(value)) {
      setHasEmailError(false);
    } else {
      setHasEmailError(true);
    }
  }, []);

  const handleFirstNameBlur = useCallback(
    (e) => {
      const userId = user.id;

      if (!hasFirstNameError && firstName && userFirstName !== firstName) {
        updateCustomerInfo({ firstName }, userId);
      }
    },
    [user, firstName, userFirstName, hasFirstNameError, updateCustomerInfo]
  );

  const handleLastNameBlur = useCallback(
    (e) => {
      const userId = user.id;

      if (!hasLastNameError && lastName && userFamilyName !== email) {
        updateCustomerInfo({ lastName }, userId);
      }
    },
    [user, lastName, userFamilyName, hasLastNameError, updateCustomerInfo]
  );

  const handleEmailBlur = useCallback(
    (e) => {
      const userId = user.id;

      if (!hasEmailError && email && userEmail !== email) {
        updateCustomerInfo({ email }, userId);
      }
    },
    [user, email, userEmail, hasEmailError, updateCustomerInfo]
  );

  const handleChangePasswordClick = useCallback(() => {
    openChangePasswordModal();

    setAuthState("passwordReset");
  }, []); // eslint-disable-line

  const handleDeleteAccount = useCallback(() => {
    const userId = user.id;

    deleteCustomerAccount(userId)
      .then(() => {
        signOut();
      })
      .catch((err) => {
        if (err) {
          openInfoModal(err.message);
        }
      });
  }, [user]);

  const handleNotificationsConsentChange = useCallback(() => {
    setNotificationsConsent(!notificationsConsent);
  }, [notificationsConsent]);

  const handleLanguageChange = useCallback(
    (id) => {
      onSelectLanguage(id);
    },
    [onSelectLanguage]
  );

  const handleLanguageSave = useCallback(() => {
    const userId = user.id;
    const region = normalizeLanguageCode(selectedLanguage);

    updateCustomerInfo({ region }, userId);
    closeChangeRegionModal();
  }, [user, selectedLanguage]);

  return (
    <>
      <ScrollToTopOnMount />
      <div className="tbk-mt-4 lg:tbk-mt-5 lg:tbk-grid lg:tbk-grid-cols-2 lg:tbk-items-start lg:tbk-gap-5">
        <div>
          <div className="tbk-mb-2 tbk-rounded-2xl tbk-bg-basic-white tbk-p-3 tbk-shadow-trube md:tbk-mb-5">
            <div className="tbk-text-h3-subtitle md:tbk-text-title-bold tbk-mb-0.5 tbk-text-primary">
              Your Phone Number:
            </div>
            <p className="tbk-text-main tbk-mb-3 tbk-text-primary">
              This is a phone number linked to your account.
            </p>
            <div className="tbk-mb-1 tbk-flex">
              <PhoneCodeInput
                theme={theme}
                className="tbk-mr-1"
                inputClassName="tbk-text-primary tbk-bg-grey-light"
                countryFlag={countryFlag}
                phoneCode={phoneCode}
              />
              <div className="tbk-input-field lg:tbk-text-title-medium tbk-text-h3-subtitle tbk-mb-0 tbk-flex tbk-h-7 tbk-min-w-0 tbk-grow tbk-items-center tbk-whitespace-nowrap tbk-bg-grey-light tbk-py-2 tbk-pr-2 tbk-text-primary">
                <div className="tbk-overflow-hidden tbk-text-ellipsis ">
                  {userPhoneNumber && formatPhoneNumber(userPhoneNumber)}
                </div>
              </div>
            </div>
            <div className="tbk-mb-3 tbk-hidden">
              <input
                id={"notificationsConsent"}
                type="checkbox"
                name="notificationsConsent"
                checked={notificationsConsent}
                onChange={handleNotificationsConsentChange}
              />
              <label
                htmlFor={"notificationsConsent"}
                className="tbk-flex tbk-h-3 tbk-items-center tbk-pl-4"
              >
                <div className="tbk-text-small tbk-text-primary">
                  I don't want to receive TruBe offers and news via SMS.
                </div>
              </label>
            </div>
            <Button variant="secondary" className="tbk-hidden tbk-w-full">
              Change Phone Number
            </Button>
          </div>

          <div className="tbk-mb-2 tbk-rounded-2xl tbk-bg-basic-white tbk-p-3 tbk-shadow-trube md:tbk-mb-2">
            <div className="tbk-text-h3-subtitle md:tbk-text-title-bold tbk-mb-0.5 tbk-text-primary">
              Medical Information:
            </div>
            <p className="tbk-text-main tbk-mb-3 tbk-text-primary">
              Add information about yor medical condition or injuries, so our{" "}
              <FormattedMessage id="trainers" defaultMessage="trainers" /> may
              build a correct workout plan for you.
            </p>
            <div className="tbk-text-small tbk-mb-0.5 tbk-text-primary">
              Your Medical Information:
            </div>
            <textarea
              ref={medicalInformationRef}
              name="medicalInformation"
              id="medicalInformation"
              className={`tbk-text-main tbk-w-full tbk-rounded-lg tbk-border tbk-border-solid tbk-px-2 tbk-py-1.5 tbk-text-primary ${
                medicalInformationError
                  ? "tbk-border-coral"
                  : "tbk-border-blue-grey-light"
              }`}
              placeholder="Condition, injures, etc"
              cols="32"
              rows="8"
              value={medicalInformation}
              onChange={handleMedicalInformationChange}
              onBlur={handleMedicalInformationBlur}
            />
          </div>
          {medicalInformationError && (
            <div className="tbk-text-small tbk-mt-1 tbk-text-coral">
              {medicalInformationError}
            </div>
          )}
        </div>

        <div className="tbk-p-">
          <div className="tbk-mb-5 tbk-rounded-2xl tbk-bg-basic-white tbk-p-3 tbk-shadow-trube">
            <div className="tbk-text-h3-subtitle md:tbk-text-title-bold tbk-mb-0.5 tbk-text-primary">
              Your Personal Data:
            </div>
            <p className="tbk-text-main tbk-mb-3 tbk-text-primary">
              Your actual personal information is required for providing you
              services.
            </p>
            <div className="tbk-text-small tbk-mb-0.5 tbk-text-primary">
              Your First Name:
            </div>
            <input
              type="text"
              className={`tbk-text-main tbk-mb-2 tbk-w-full tbk-rounded-lg tbk-border ${
                hasFirstNameError
                  ? "tbk-border-coral"
                  : "tbk-border-blue-grey-light"
              } tbk-bg-basic-white tbk-px-2 tbk-py-1  ${
                hasFirstNameError ? "tbk-text-coral" : "tbk-text-primary"
              }`}
              value={firstName}
              minLength={2}
              pattern="\s*(\S\s*){2,}"
              onChange={handleFirstNameChange}
              onBlur={handleFirstNameBlur}
            />

            <div className="tbk-text-small tbk-mb-0.5 tbk-text-primary">
              Your Last Name:
            </div>
            <input
              type="text"
              className={`tbk-text-main tbk-mb-2 tbk-w-full tbk-rounded-lg tbk-border ${
                hasLastNameError
                  ? "tbk-border-coral"
                  : "tbk-border-blue-grey-light"
              } tbk-bg-basic-white tbk-px-2 tbk-py-1 ${
                hasLastNameError ? "tbk-text-coral" : "tbk-text-primary"
              }`}
              minLength={2}
              pattern="\s*(\S\s*){2,}"
              value={lastName}
              onChange={handleLastNameChange}
              onBlur={handleLastNameBlur}
            />

            <div className="tbk-text-small tbk-mb-0.5 tbk-text-primary">
              Your Email:
            </div>
            <input
              type="email"
              className={`tbk-text-main tbk-w-full tbk-text-ellipsis tbk-rounded-lg tbk-border ${
                hasEmailError
                  ? "tbk-border-coral"
                  : "tbk-border-blue-grey-light"
              } tbk-bg-basic-white tbk-px-2 tbk-py-1 ${
                hasEmailError ? "tbk-text-coral" : "tbk-text-primary"
              }`}
              value={email}
              onChange={handleEmailChange}
              onBlur={handleEmailBlur}
            />
          </div>

          <Button
            variant="secondary"
            className="tbk-mb-2 tbk-w-full"
            endIcon={<GlobeIcon />}
            onClick={openModal}
          >
            Change Region
          </Button>
          {/*<Button
            variant="secondary"
            className="tbk-mb-2 tbk-w-full"
            endIcon={<LockIcon />}
            onClick={handleChangePasswordClick}
          >
            Change Password
          </Button>*/}
          <Button
            variant="secondary"
            className="tbk-mb-5 tbk-w-full"
            endIcon={<LogoutIcon />}
            onClick={() => signOut()}
          >
            Log Out
          </Button>
          <div className="tbk-text-center">
            <button
              className="tbk-text-secondary tbk-text-blue-grey tbk-underline"
              onClick={openDeleteAccountModal}
            >
              I want to delete my account
            </button>
          </div>
        </div>
      </div>

      <Modal
        isOpen={isChangePasswordModalOpen}
        // onAfterOpen={afterOpenModal}
        onRequestClose={closeChangePasswordModal}
        style={modalCustomStyle}
        contentLabel="Change Password Modal"
      >
        <button
          onClick={closeChangePasswordModal}
          className="tbk-absolute tbk-right-2 tbk-top-2 tbk-z-10 tbk-text-coral"
        >
          <CloseIcon />
        </button>
        <div className="modal-change-password">
          {authState === "passwordReset" ? (
            <PasswordConfirmForm
              phoneCode={phoneCode}
              phoneNumber={userPhoneNumber}
            />
          ) : (
            <PasswordResetCodeVerification
              phoneCode={phoneCode}
              phoneNumber={userPhoneNumber}
            />
          )}
        </div>
      </Modal>
      <Modal
        isOpen={isDeleteAccountModalOpen}
        // onAfterOpen={afterOpenModal}
        onRequestClose={closeDeleteAccountModal}
        style={modalCustomStyle}
        contentLabel="Delete Account"
      >
        <button
          onClick={closeDeleteAccountModal}
          className="tbk-absolute tbk-right-2 tbk-top-2 tbk-z-10 tbk-text-coral"
        >
          <CloseIcon />
        </button>
        <div className="tbk-text-title-bold tbk-mb-1 tbk-text-grey-main">
          Are You Sure?
        </div>
        <p className="tbk-text-main tbk-mb-4 tbk-text-grey-main">
          Are you sure you want to delete your TruBe account with all your data
          (personal info, contacts, payment methods)?
        </p>
        <Button
          color="coral"
          className="tbk-mb-2 tbk-w-full"
          onClick={closeDeleteAccountModal}
        >
          No, go back
        </Button>
        <Button
          variant="secondary"
          className="tbk-w-full"
          onClick={handleDeleteAccount}
        >
          Delete my account
        </Button>
      </Modal>
      <Modal
        appElement={/*__CLIENT__ && */ document.getElementById("#app")}
        isOpen={isInfoModalOpen}
        // onAfterOpen={afterOpenInfoModal}
        onRequestClose={closeInfoModal}
        style={
          clientWidth() < theme.responsive.breakpoints.sm
            ? modalStyles.infoMobile
            : modalStyles.info
        }
        contentLabel="Info Modal"
      >
        <div className="modal-info tbk-flex tbk-flex-col tbk-items-center">
          <button
            onClick={closeInfoModal}
            className="tbk-absolute tbk-right-2 tbk-top-2 tbk-z-10 tbk-hidden tbk-text-coral"
          >
            <CloseIcon />
          </button>
          <h3 className="tbk-text-title-bold tbk-mb-1 tbk-mt-0 tbk-text-center tbk-text-grey-main">
            {infoModalTitle}
          </h3>
          <p className="tbk-text-main tbk-mb-4 tbk-max-w-full tbk-text-center tbk-text-grey-main">
            {infoModalMsg}
          </p>
          {error ? (
            <BlockerIcon size={40} className="tbk-mb-4 tbk-text-green" />
          ) : (
            <CheckNiceIcon size={40} className="tbk-mb-4 tbk-text-green" />
          )}
          <Button className="tbk-w-full" onClick={closeInfoModal}>
            Ok
          </Button>
        </div>
      </Modal>
    </>
  );
};

export default AccountSettings;
